import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMe, useRaffles, useScrapers, useSystemSummary } from '../../api/queries';
import { useReconcileScraperFleet } from '../../api/mutations';
import { Ic } from '../../components/Ic';
import { Seg } from '../../components/Seg';
import { Empty } from '../../components/Empty';
import { RaffleRow } from '../../components/RaffleRow';
import { usePresence } from '../../components/usePresence';
import { deriveScraperState, isAttentionWorthy } from '../../lib/scraperState';
import { useNow } from '../../lib/countdown';
import { RaffleDetailModal } from '../Raffles/RaffleDetailModal';
import { RaffleCard } from './RaffleCard';

const SORTS = [
  { value: 'closing' as const, label: 'Closing soon' },
  { value: 'new' as const, label: 'Newest' },
];

/** Home. Open raffles are the hero; the engine's health is summarised in the
 *  header line and the attention banners, with detail one click away. */
export function BoardView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const sort = params.get('sort') === 'new' ? 'new' : 'closing';

  const { data: open, error, isLoading } = useRaffles({
    status: 'posted',
    active: true,
    sort: sort === 'new' ? 'detected' : 'closes',
    order: sort === 'new' ? 'desc' : 'asc',
    limit: 48,
  });
  const { data: recent } = useRaffles({ sort: 'detected', order: 'desc', limit: 10 });
  const { data: scrapers } = useScrapers();
  const { data: summary } = useSystemSummary();
  const { data: me } = useMe();
  const reconcile = useReconcileScraperFleet();

  const items = open?.items ?? [];
  // Seconds only matter once something closes within a day.
  const needsSeconds = items.some((r) => r.cutoff && r.cutoff * 1000 - Date.now() < 86_400_000);
  const now = useNow(needsSeconds ? 1000 : 30_000);

  const attention = scrapers?.filter(isAttentionWorthy) ?? [];
  const offline = (scrapers ?? []).filter((s) => s.enabled && s.runner_alive === false);
  const healthy = scrapers?.filter((s) => deriveScraperState(s) === 'healthy').length ?? 0;

  const selected = id
    ? [...items, ...(recent?.items ?? [])].find((r) => String(r.id) === id)
    : undefined;
  const detail = usePresence(selected ? { raffle: selected } : undefined);
  const openRaffle = (raffleId: number) =>
    navigate({ pathname: `/raffle/${raffleId}`, search: params.toString() });

  return (
    <div className="view-pad board">
      <header className="page-head">
        <div>
          <h1>Board</h1>
          <p className="page-sub">
            <span className="num">{open ? open.total : '—'}</span> open
            <span className="dot-sep" />
            <span className="num">{summary?.sent_today ?? '—'}</span> sent today
            <span className="dot-sep" />
            <span className="num">{scrapers ? `${healthy}/${scrapers.length}` : '—'}</span> scrapers healthy
          </p>
        </div>
        <Seg
          value={sort}
          onChange={(next) => {
            const p = new URLSearchParams(params);
            if (next === 'closing') p.delete('sort');
            else p.set('sort', next);
            setParams(p, { replace: true });
          }}
          options={SORTS}
          ariaLabel="Sort"
        />
      </header>

      {attention.length > 0 && (
        <div className="pcard tone-err board-banner">
          <div className="banner">
            <Ic.zap className="ic" />
            <div className="main">
              <div className="t">
                {attention.length} scraper{attention.length > 1 ? 's' : ''} need attention
              </div>
              <div className="m">{attention.map((s) => s.name).join(', ')}</div>
            </div>
            <button className="btn sm" onClick={() => navigate('/scrapers?state=down')}>
              Review <Ic.arrowRight className="ico" />
            </button>
          </div>
        </div>
      )}
      {offline.length > 0 && (
        <div className="pcard tone-warn board-banner">
          <div className="banner">
            <Ic.alert className="ic" />
            <div className="main">
              <div className="t">{offline.length} runner{offline.length > 1 ? 's' : ''} offline</div>
              <div className="m">{offline.map((s) => s.name).join(', ')}</div>
            </div>
            {me?.is_super_admin && (
              <button className="btn sm" onClick={() => reconcile.mutate()} disabled={reconcile.isPending}>
                Reconcile fleet
              </button>
            )}
          </div>
        </div>
      )}

      <div className="board-layout">
        <section aria-label="Open raffles">
          {error ? (
            <Empty
              icon={Ic.alert}
              title="Could not load raffles"
              detail={error instanceof Error ? error.message : String(error)}
            />
          ) : isLoading ? (
            <div className="muted loading-delayed">Loading raffles…</div>
          ) : items.length === 0 ? (
            <Empty icon={Ic.tag} title="No open raffles" detail="New raffles land here the moment a scraper finds one." />
          ) : (
            <div className="rgrid">
              {items.map((r) => (
                <RaffleCard key={r.id} raffle={r} now={now} onOpen={() => openRaffle(r.id)} />
              ))}
            </div>
          )}
        </section>

        <aside className="pcard board-feed" aria-label="Just detected">
          <div className="head">
            <Ic.history className="ic" />
            <h3>Just detected</h3>
            <span className="spacer" />
            <span className="live-dot" aria-hidden />
          </div>
          <div className="body">
            {recent?.items.length ? (
              recent.items.map((r) => <RaffleRow key={r.id} raffle={r} onClick={() => openRaffle(r.id)} />)
            ) : (
              <div className="muted board-feed-empty">Nothing detected yet.</div>
            )}
          </div>
          <button className="btn ghost sm board-feed-more" onClick={() => navigate('/raffles')}>
            All raffles <Ic.arrowRight className="ico" />
          </button>
        </aside>
      </div>

      {detail.item && (
        <RaffleDetailModal
          {...detail.item}
          open={detail.open}
          onExited={detail.onExited}
          closeTo={{ pathname: '/', search: params.toString() }}
        />
      )}
    </div>
  );
}
